
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { DirectionT } from '@/core/types/directions.types'
import { CityT, ZoneT } from '@/core/types/cities.types'

// composable
import useVuelidate from '@vuelidate/core'
import { CreateDirectionValidation } from '@/modules/DirectionsModule/validations/CreateDirection.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
        'the-input': TheInput,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<DirectionT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            deliveryTime: null as number | null,
            fromCityId: null as number | null,
            whereCityId: null as number | null,
            isActive: 0,
            is_nearby_cities: 0,
            zone_id: null as number | null,
            price: null as number | null,
        })

        const data_state = reactive({
            from_cities: new DataState<CityT>({}),
            where_cities: new DataState<CityT>({}),
            zones: new DataState<ZoneT>({}),
        })

        const state_form$ = useVuelidate(CreateDirectionValidation, state)

        const loadWhereCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.where_cities.pagination.current_page = page

            try {
                data_state.where_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.where_cities.list = data.data

                data_state.where_cities.pagination.total_pages = data.meta.last_page

                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.where_cities.failure.catchError(error)

                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const loadFromCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.from_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadZones = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.zones.pagination.current_page = page

            try {
                data_state.zones.is_loading = true

                const data = await cities.getZonesList(page, search)
                data_state.zones.list = data.data

                data_state.zones.pagination.total_pages = data.meta.last_page

                data_state.zones.is_failed = false
                data_state.zones.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.zones.failure.catchError(error)

                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_failed = true
                }
            } finally {
                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_loading = false
                }
            }
        }

        const loadMoreWhereCities = async (page: number) => {
            if (page > data_state.where_cities.pagination.total_pages) return
            data_state.where_cities.pagination.current_page = page

            try {
                data_state.where_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.where_cities.list = data_state.where_cities.list.concat(response.data)

                data_state.where_cities.pagination.total_pages = response.meta.last_page

                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.where_cities.failure.catchError(error)

                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const loadMoreFromCities = async (page: number) => {
            if (page > data_state.from_cities.pagination.total_pages) return
            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.from_cities.list = data_state.from_cities.list.concat(response.data)

                data_state.from_cities.pagination.total_pages = response.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadMoreZones = async (page: number) => {
            if (page > data_state.zones.pagination.total_pages) return
            data_state.zones.pagination.current_page = page

            try {
                data_state.zones.is_loading = true

                const response = await cities.getZonesList(page)
                data_state.zones.list = data_state.zones.list.concat(response.data)

                data_state.zones.pagination.total_pages = response.meta.last_page

                data_state.zones.is_failed = false
                data_state.zones.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.zones.failure.catchError(error)

                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_failed = true
                }
            } finally {
                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_loading = false
                }
            }
        }

        const searchWhereCity = (search_query: string) => {
            // data_state.where_cities.pagination.current_page
            loadWhereCities(1, search_query)
        }

        const searchFromCity = (search_query: string) => {
            // data_state.from_cities.pagination.current_page
            loadFromCities(1, search_query)
        }

        const searchZone = (search_query: string) => {
            loadZones(1, search_query)
        }

        const loadCities = async () => {
            try {
                data_state.from_cities.is_loading = true
                data_state.where_cities.is_loading = true

                const data = await cities.getAllList(1)

                data_state.from_cities.list = data.data
                data_state.where_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page
                data_state.where_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.where_cities.is_failed = false

                data_state.from_cities.failure.error_state.is_cancel = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)
                data_state.where_cities.failure.catchError(error)

                if (
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.where_cities.failure.error_state.is_cancel
                ) {
                    data_state.from_cities.is_failed = true
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.where_cities.failure.error_state.is_cancel
                ) {
                    data_state.from_cities.is_loading = false
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const unshiftFindedFromCity = async (city: CityT) => {
            await loadFromCities(data_state.from_cities.pagination.current_page)
            data_state.from_cities.list.unshift(city)
        }

        const unshiftFindedZone = async (zone: ZoneT) => {
            await loadZones(data_state.zones.pagination.current_page)
            data_state.zones.list.unshift(zone)
        }

        const unshiftFindedWhereCity = async (city: CityT) => {
            await loadWhereCities(data_state.where_cities.pagination.current_page)
            data_state.where_cities.list.unshift(city)
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление направления'
            if (props.isUpdate) return 'Редактирование направления'
            if (props.isDelete) return 'Удаление направления'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        onBeforeMount(async () => {
            loadCities()
            loadZones(1)
            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.deliveryTime = props.initialValue.deliveryTime
            state.fromCityId = props.initialValue.fromCityId
            state.whereCityId = props.initialValue.whereCityId
            state.isActive = props.initialValue.isActive
            state.price = props.initialValue.price

            state.is_nearby_cities = props.initialValue.isNearbyCities
            state.zone_id = props.initialValue.zoneId

            state_form$.value.$reset()
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', {
                id: state.id,
                zoneId: state.zone_id,
                isNearbyCities: state.is_nearby_cities,
            })
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
        }

        return {
            state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitCancel,
            emitManager,
            loadWhereCities,
            loadMoreWhereCities,
            searchWhereCity,
            loadFromCities,
            loadMoreFromCities,
            searchFromCity,
            searchZone,
            unshiftFindedFromCity,
            unshiftFindedZone,
            unshiftFindedWhereCity,
            loadZones,
            loadMoreZones,
        }
    },
})
